import React from 'react';
import { Typography, Paper, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LockIcon from '@mui/icons-material/Lock';
import GroupIcon from '@mui/icons-material/Group';

const UserManagement = () => {
  const userManagementFeatures = [
    {
      icon: <PersonAddIcon />,
      title: "Add New Users",
      description: "Invite new team members to join your AI Talker platform with customized roles and permissions."
    },
    {
      icon: <EditIcon />,
      title: "Edit User Profiles",
      description: "Modify existing user information, update roles, or change access levels as needed."
    },
    {
      icon: <DeleteIcon />,
      title: "Remove Users",
      description: "Securely remove users who no longer require access to the AI Talker system."
    },
    {
      icon: <LockIcon />,
      title: "Manage Permissions",
      description: "Set up and customize user roles with specific permissions to control access to different parts of the platform."
    },
    {
      icon: <GroupIcon />,
      title: "Team Structure",
      description: "Organize users into teams or departments for easier management and collaboration."
    }
  ];

  return (
    <Paper elevation={0}>
      <Typography variant="h4" gutterBottom>User Management</Typography>
      <Typography paragraph>
        The User Management section provides administrators with powerful tools to control access to the AI Talker platform. 
        Efficiently manage your team's access, roles, and permissions to ensure secure and effective use of the system.
      </Typography>

      <List>
        {userManagementFeatures.map((feature, index) => (
          <ListItem key={index}>
            <ListItemIcon>
              {feature.icon}
            </ListItemIcon>
            <ListItemText 
              primary={feature.title}
              secondary={feature.description}
            />
          </ListItem>
        ))}
      </List>

      <Typography variant="h6" style={{ marginTop: '20px' }}>Key User Management Tasks:</Typography>
      <Typography paragraph>
        1. <strong>Adding Users:</strong> Invite new team members by entering their email address and assigning appropriate roles.
      </Typography>
      <Typography paragraph>
        2. <strong>Defining Roles:</strong> Create and customize roles with specific permissions tailored to your organization's needs.
      </Typography>
      <Typography paragraph>
        3. <strong>Modifying Access:</strong> Adjust user permissions as responsibilities change or for temporary project needs.
      </Typography>
      <Typography paragraph>
        4. <strong>User Deactivation:</strong> Quickly revoke access for users who no longer need it, ensuring system security.
      </Typography>
      <Typography paragraph>
        5. <strong>Activity Monitoring:</strong> Review user activity logs to ensure proper system usage and identify any unusual behavior.
      </Typography>

      <Typography variant="h6" style={{ marginTop: '20px' }}>Best Practices for User Management:</Typography>
      <Typography paragraph>
        - Regularly review user access and permissions to ensure they align with current roles and responsibilities.
      </Typography>
      <Typography paragraph>
        - Implement the principle of least privilege, granting users only the permissions they need to perform their tasks.
      </Typography>
      <Typography paragraph>
        - Use role-based access control (RBAC) to simplify permission management for groups of users with similar needs.
      </Typography>
      <Typography paragraph>
        - Maintain an audit trail of user management activities for security and compliance purposes.
      </Typography>
      <Typography paragraph>
        - Provide clear guidelines and training for users on their roles and responsibilities within the AI Talker platform.
      </Typography>

      <Typography paragraph style={{ marginTop: '20px' }}>
        Effective user management is crucial for maintaining the security and efficiency of your AI Talker implementation. 
        By carefully controlling access and permissions, you can ensure that your team members have the tools they need 
        while protecting sensitive information and system integrity.
      </Typography>
      <Typography paragraph>
        If you need assistance with user management or have questions about best practices, our support team is always 
        available to help. We can provide guidance on setting up complex permission structures or assist with bulk user 
        management for larger organizations.
      </Typography>
    </Paper>
  );
};

export default UserManagement;
